import { useQuery } from '@tanstack/react-query';

import { logError } from 'lib/sentry/logError';
import libheif from 'libheif-js';

const HEIC_FORMATS = ['image/heic', 'image/heif'];

async function fetchImage(url: string): Promise<Uint8Array> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.statusText}`);
  }
  const arrayBuffer = await response.arrayBuffer();

  return new Uint8Array(arrayBuffer);
}

async function decodeHeic(data: Uint8Array): Promise<libheif.HeifImage> {
  const decoder = new libheif.HeifDecoder();
  const images = decoder.decode(data);
  if (!images.length) {
    throw new Error('No images found in HEIF file');
  }

  return images[0];
}

async function renderToJpeg(image: libheif.HeifImage): Promise<string> {
  const width = image.get_width();
  const height = image.get_height();

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not get canvas context');
  }

  try {
    const imageData = ctx.createImageData(width, height);
    const displayData = await new Promise<ImageData>((resolve, reject) => {
      image.display(imageData, (result: ImageData | null) => {
        if (!result) {
          reject(new Error('Failed to decode image data'));
        } else {
          resolve(result);
        }
      });
    });
    ctx.putImageData(displayData, 0, 0);
    return canvas.toDataURL('image/jpeg', 0.8);
  } finally {
    image.free();
  }
}

async function fetchAndConvertImage(
  url: string,
  contentType: string
): Promise<string> {
  if (!HEIC_FORMATS.includes(contentType.toLowerCase())) {
    return url;
  }

  const imageData = await fetchImage(url);
  const image = await decodeHeic(imageData);

  return renderToJpeg(image);
}

type TUseHEICImage = {
  src: string;
  contentType: string;
};

export const useHEICImage = ({ src, contentType }: TUseHEICImage) => {
  const { data: imageSrc, isLoading } = useQuery({
    queryKey: ['image', src, contentType],
    queryFn: () => fetchAndConvertImage(src, contentType),
    staleTime: Infinity,
    cacheTime: Infinity,
    onError: (error) => {
      logError(error);
      console.error('Error during image fetch or conversion:', error);
    },
  });

  return { data: imageSrc || src, isLoading };
};
